'use client';

import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { FocusScope } from 'react-aria';
import { useMinLoadingState } from 'components/DropZone';
import { AnimatePresence } from 'framer-motion';
import {
  GlobalErrorContext,
  type GlobalErrorContextType,
} from 'hooks/useGlobalErrorContext';
import { useContextSelector } from 'use-context-selector';
import { ToastContents } from './ToastAlertContents';
import { ToastAnimationDisplay } from './ToastAnimationDisplay';
import { ToastContext } from './ToastContext';
import { ToastItemContext } from './ToastItemContext';
import { ToastLoading } from './ToastLoading';
import { type ToastAlertProps } from './ToastTypes';

export function ToastAlert({
  title,
  details,
  variant = 'neutral',
  onRequestClose,
  closeLabel,
  adornmentBefore,
  adornmentAfter,
  initialFocusRef,
  isLoading: isLoadingProp = false,
  ...otherProps
}: ToastAlertProps) {
  const { duration, onDismiss } = useContext(ToastItemContext);
  const { defaultCloseLabel } = useContext(ToastContext);
  const isLoading = useMinLoadingState(isLoadingProp, 750);
  const ref = useRef<HTMLDivElement>(null);
  const hasGlobalErrorState = useContextSelector(
    GlobalErrorContext,
    (state: GlobalErrorContextType) => state.hasGlobalErrorState
  );
  const hideForGlobalError = variant === 'error' && hasGlobalErrorState;

  useEffect(() => {
    initialFocusRef?.current?.focus();
  }, [initialFocusRef]);

  const handleClose = useCallback(() => {
    onRequestClose?.();
    onDismiss();
  }, [onRequestClose, onDismiss]);

  if (hideForGlobalError) return null;

  const autoFocus: boolean =
    duration === null || Boolean(initialFocusRef?.current);

  const effectiveCloseLabel = closeLabel ?? defaultCloseLabel;

  return (
    <ToastAnimationDisplay isLoading={isLoadingProp} toastRef={ref}>
      <AnimatePresence initial={false}>
        {isLoading ? (
          <ToastLoading
            key="loading"
            ref={ref}
            details={details}
            isLoading={isLoadingProp}
            title={title}
          />
        ) : (
          <FocusScope autoFocus={autoFocus} restoreFocus>
            <ToastContents
              key="content"
              ref={ref}
              adornmentAfter={adornmentAfter}
              adornmentBefore={adornmentBefore}
              closeLabel={effectiveCloseLabel}
              details={details}
              isLoading={isLoading}
              onRequestClose={handleClose}
              title={title}
              variant={variant}
              {...otherProps}
            />
          </FocusScope>
        )}
      </AnimatePresence>
    </ToastAnimationDisplay>
  );
}
